/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { IMenuItemData, INavigationMenuViewProps } from '@msdyn365-commerce-modules/navigation-menu';
// import { generateImageUrl } from '@msdyn365-commerce-modules/retail-actions';
import { addThrottledEvent, getPayloadObject, getTelemetryAttributes, getTelemetryObject, IPayLoad, ITelemetryContent, Module, Node, onTelemetryClick } from '@msdyn365-commerce-modules/utilities';
import MsDyn365, { ILinkData as MsdynILinkData } from '@msdyn365-commerce/core';
import classnames from 'classnames';
import { computed } from 'mobx';
import * as React from 'react';
import { Drawer } from './components/citta-drawer';
import { removeDomainQspFromUrl } from '../../../../src/actions/utilities/utils';

interface INavigationState {
  parentMenu?: number;
  activeMenu?: number;
  activeDesktopMenu?: number;
  mobileViewLabelText?: string;
  cateGoryImageURL: string;
  onlyMobile: boolean;
  desktopCategoryId: number;
  isDrawerOpen: boolean;
  menuCurrentLevel: number[];
  openFirstByDefault: boolean;
  hasUserInteracted: boolean;
}
export interface IMenuItemsData {
  id?: number;
  linkText?: string;
  linkURL?: string;
  imageSource?: string;
  imageDestinationURL?: string;
  subMenu?: IMenuItemsData[];
  menuLevel?: number;
  parent1Id?: number;
  parent2Id?: number;
  parent3Id?: number;
  levelIds?: number[];
}

export type GridSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
/**
 *
 * NavigationMenuView component
 * @extends {React.PureComponent<INavigationMenuViewProps>}
 */
export class NavigationMenuView extends React.PureComponent<INavigationMenuViewProps, INavigationState> {
  private supportedLevel: number = 4;
  private currentLevel: number = 0;
  private escapeKey: number = 27;
  private menuNode: React.RefObject<HTMLUListElement>;
  private viewport: GridSize;
  private telemetryContent: ITelemetryContent;
  private payLoad: IPayLoad;
  private coustomResizeThrottledEventHandler?: (event: Event) => void;
  private menuItems: IMenuItemData[];
  constructor(props: INavigationMenuViewProps) {
    super(props);
    this.menuNode = React.createRef();

    // @ts-ignore
    let openFirstByDefault = props.config.openFirstMenuItem;
    this.state = {
      activeMenu: undefined,
      mobileViewLabelText: '',
      parentMenu: undefined,
      cateGoryImageURL: '',
      onlyMobile: this.onlyMobile,
      desktopCategoryId: -1,
      isDrawerOpen: false,
      menuCurrentLevel: [],
      activeDesktopMenu: undefined,
      openFirstByDefault: openFirstByDefault,
      hasUserInteracted: false
    };
    this.viewport = this._customGetViewport();
    this._closeSubmenu = this._closeSubmenu.bind(this);
    this._escFunction = this._escFunction.bind(this);
    this._customUpdateViewport = this._customUpdateViewport.bind(this);
    this.telemetryContent = getTelemetryObject(this.props.context.request.telemetryPageName!,
      this.props.friendlyName,
      this.props.telemetry);
    this.payLoad = getPayloadObject('click', this.telemetryContent, '', '');

    this.menuItems = props.menuItemData
  }
  @computed get onlyMobile(): boolean {
    return (this.viewport === 'xs');
  }
  public componentDidMount(): void {
    document.body && document.addEventListener('mousedown', this._handleClickOutside);
    this.coustomResizeThrottledEventHandler =
      window && addThrottledEvent(window, 'resize', this._customUpdateViewport as EventListener);
    this._customUpdateViewport();
    document.body && document.addEventListener('keydown', (this._escFunction as unknown) as EventListener, false);
  }

  public componentWillUnmount(): void {
    document.body && document.removeEventListener('keydown', (this._escFunction as unknown) as EventListener, false);
    window && window.removeEventListener('resize', this.coustomResizeThrottledEventHandler!, false);
    // document.body && document.removeEventListener('mousedown', this._handleClickOutside, false);
  }

  public componentDidUpdate(hello: any, state: any): void {

    if (!state.activeMenu && !this.state.activeMenu && window.document.body.classList.contains('menu-open')) {
      window.document.body.classList.toggle('menu-open', false);
    }
  }

  public render(): JSX.Element | null {
    const { isMobileView } = this.props;
    this.currentLevel = 1;
    const showOverlay = this._shouldRenderOverlays();
    return (
      <React.Fragment>
        <div className={`nav-menu-overlay ${showOverlay ? 'isVisable' : 'notShowing'}`} />
        {isMobileView && this.state.onlyMobile
          ? this._renderMobileMenu()
          : this._renderDesktopMenu()
        }
      </React.Fragment>
    );
  }

  private _renderMobileMenu(): JSX.Element {
    const { isMobileView, Navigation } = this.props;
    const _menuItems: IMenuItemsData[] = this.menuItems;

    const filteredMenuData = _menuItems.map(p => {
      p.menuLevel = 1;
      p.parent1Id = -1;
      p.parent2Id = -1;
      p.parent3Id = -1;
      p.levelIds = [p.id!];
      p.subMenu = p.subMenu?.filter(a => a.linkText?.toLowerCase() !== 'all');
      p.subMenu?.map(x => {
        x.menuLevel = 2;
        x.parent1Id = p.id;
        x.parent2Id = -1;
        x.parent3Id = -1;
        x.levelIds = [x.id!, p.id!];
        x.subMenu?.map(t => {
          t.menuLevel = 3;
          t.parent1Id = x.id;
          t.parent2Id = p.id;
          t.parent3Id = -1;
          t.levelIds = [x.id!, p.id!, t.id!];
          t.subMenu?.map(u => {
            u.menuLevel = 4;
            u.parent1Id = t.id;
            u.parent2Id = x.id;
            u.parent3Id = p.id;
            u.levelIds = [x.id!, p.id!, t.id!, u.id!];
          })
        });
      });
      return p;
    }
    );
    return (
      <Module {...Navigation} className={classnames(Navigation.className, isMobileView && this.state.onlyMobile ? 'mobile-view' : 'desktop-view')}>
        {filteredMenuData.map((menuItem: IMenuItemsData, index: number) => {
          return this._renderDrawerMenu(menuItem, menuItem.menuLevel, menuItem.linkText === 'Shop' ? 0 : undefined);
        })}
      </Module>
    );
  }

  private _shouldRenderOverlays(): boolean {
    const { activeMenu, isDrawerOpen } = this.state;
    const { menuItemData } = this.props;
    if (isDrawerOpen) {
      return true;
    }
    if (activeMenu) {
      const menuItem = menuItemData.find(menuItem => menuItem.id === activeMenu)

      return menuItem ? true : false;
    }

    return false;
  }

  private _renderDesktopMenu(): JSX.Element {
    const { MenuList, Navigation } = this.props;
    const showOverlay = this._shouldRenderOverlays();
    return (
      <Module {...Navigation} className={classnames(Navigation.className, showOverlay ? '' : 'should-menu-closed')}>
        <Node {...MenuList} ref={this.menuNode} tabIndex='-1'>
          {this._renderDisplay()}
          {<div className={`underlay ${showOverlay ? 'isVisable' : ''}`}></div>}
        </Node>
      </Module>
    );
  }

  private _renderDrawerMenu(menuItem: IMenuItemsData, mobileLevel?: number, parentIndex?: number): JSX.Element | null {
    if (menuItem && menuItem.subMenu && menuItem.subMenu.length > 0 && menuItem.linkText) {
      return (this._renderDrawer(menuItem, mobileLevel, parentIndex));
    } else {
      return (this._renderLinkMenuItem(menuItem));
    }
  }

  private _renderDrawer(menuItem: IMenuItemsData, mobileLevel?: number, parentIndex?: number): JSX.Element | null {
    const { openFirstByDefault, hasUserInteracted } = this.state;
    const toggleButtonText = menuItem.linkText;
    let _setCollapse: boolean | undefined = menuItem.id && this.state.menuCurrentLevel.includes(menuItem.id) || false;

    // On level one, we don't have to collapse one menu on clicking other menu
    if (menuItem.menuLevel === 1) {
      _setCollapse = undefined;
    }

    if(menuItem.menuLevel ===2 && menuItem.linkText === 'Browse'){
      _setCollapse = true;
    }
    if(menuItem.linkText === "Sale" || menuItem.linkText === "Città Essentials" || menuItem.linkText ==="Sofas & Armchairs"){
      return (
        <Drawer
          collapseProps={
            {
              timeout: 0,
              isOpen:false
            }
          }
          key={menuItem.id}
          className={`ms-nav__drawer mobile-level-${menuItem.menuLevel} mobile-drawer-hiden`}
          toggleButtonText={menuItem.menuLevel === 3 || menuItem.menuLevel === 4 || (menuItem.linkURL && menuItem.menuLevel !== 1 && menuItem.menuLevel !== 2) ? this._renderMobileLinkMenuItem(menuItem) : this._renderLinkText(toggleButtonText)}
          toggleButtonTextClick={false}
          selectedStateOptionText={''}
        >
          <div>
            {menuItem.subMenu!.map((menuSubItem: IMenuItemData, index: number) => {
              return null;
            })}
          </div>
        </Drawer>
      );
    } else {
      return (
        <Drawer
          collapseProps={
            {
              timeout: 0,
              isOpen: (openFirstByDefault && !hasUserInteracted && parentIndex === 0) || _setCollapse
            }
          }
          key={menuItem.id}
          className={`ms-nav__drawer mobile-level-${menuItem.menuLevel}`}
          openGlyph='ms-nav__drawer-open'
          closeGlyph='ms-nav__drawer-close'
          glyphPlacement='end'
          toggleButtonText={menuItem.menuLevel === 3 || menuItem.menuLevel === 4 || (menuItem.linkURL && menuItem.menuLevel !== 1 && menuItem.menuLevel !== 2) ? this._renderMobileLinkMenuItem(menuItem) : this._renderLinkText(toggleButtonText)}
          toggleButtonTextClick={false}
          onToggle={this._bindDesktopCategoryClickMobile(menuItem)}
          selectedStateOptionText={''}
        >
          <div>
            {menuItem.subMenu!.map((menuSubItem: IMenuItemData, index: number) => {
              return this._renderDrawerLink(menuSubItem, mobileLevel, index);
            })}
          </div>
        </Drawer>
      );
    }
  }

  private _renderMobileLinkMenuItem(option: IMenuItemData, index?: number): JSX.Element {
    const { Link } = this.props;
    const optionVal = option.linkText;
    const linkText = option.linkText ? option.linkText : '';
    this.payLoad.contentAction.etext = linkText;
    const attributes = getTelemetryAttributes(this.telemetryContent, this.payLoad);
    return (
      <Node {...Link} key={index} href={option.linkURL} {...attributes} onClick={onTelemetryClick(this.telemetryContent, this.payLoad, linkText)} >
        {optionVal}
      </Node>
    );
  }

  // tslint:disable-next-line: no-any
  // private _bindDesktopCategoryClickMobile = (menuItem: IMenuItemsData) => (e: any): void => {
  //   this.setState({ menuCurrentLevel: menuItem.levelIds || [] });
  // }

  private _bindDesktopCategoryClickMobile = (menuItem: IMenuItemsData) => (e: any): void => {
    console.log('click')
    if (!menuItem.levelIds && menuItem.subMenu && menuItem.subMenu.length && menuItem.id && !this.state.onlyMobile) {
      if (this.state.menuCurrentLevel.includes(menuItem.id)) {
        this.setState({ menuCurrentLevel: menuItem.levelIds || [] });
      } else {
        this.setState({ menuCurrentLevel: [menuItem.id] });
      }
    } else {
      if (menuItem.id && menuItem.levelIds && this.state.menuCurrentLevel.includes(menuItem.id)) { 
        const newArray = menuItem.levelIds || [];
        newArray.length && newArray.pop()
        this.setState({ menuCurrentLevel: newArray });
      } else {
        this.setState({ menuCurrentLevel: menuItem.levelIds || [] });
      }
    }
    if(!this.state.hasUserInteracted) {
      this.setState({ hasUserInteracted: true });
    }
  }

  private _renderLinkText(linkText: string | undefined): JSX.Element {
    return (
      <React.Fragment>
        {linkText}
      </React.Fragment>
    );
  }

  private _renderDrawerLink(item: IMenuItemData, mobileLevel?: number, parentIndex?: number): JSX.Element | null {
    if (item && item.linkText && item.linkURL && item.linkURL.length > 0) {
      if (item && item.subMenu && item.subMenu?.length > 0 && item.linkText) {
        return (this._renderDrawer(item, mobileLevel, parentIndex));
      }
      return (
        this._renderLinkMenuItem(item)
      );
    } else if (item && item.linkText && !item.linkURL) {
      return (
        this._renderSpanMenuItem(item)
      );
    }
    return null;
  }

  private _renderDisplay(): JSX.Element[] {
    const { ListItem, menuItemData, isMobileView } = this.props;
    const { activeMenu } = this.state;
    const menuItemList: JSX.Element[] = [];

    const filteredMenuData = menuItemData.map(p => {
      if (p.linkText?.toLowerCase() === 'shop') {
        p.subMenu = p.subMenu?.filter(a => a.linkText?.toLowerCase() !== 'all');
      }
      return p;
    });
    if (isMobileView && this.state.onlyMobile && activeMenu !== undefined && filteredMenuData.length > 0) {
      let menuItem: IMenuItemData = {};
      for (let i = 0; i < filteredMenuData.length; i++) {

        if (filteredMenuData[i] && filteredMenuData[i].id === activeMenu) {
          menuItem = filteredMenuData[i];


          this.setState({ parentMenu: undefined, mobileViewLabelText: filteredMenuData[i].linkText });
          break;
        }
        menuItem = this._getFromSubMenu(filteredMenuData[i]) as IMenuItemData;
        if (menuItem && menuItem.id === activeMenu) {
          break;
        }
      }

      menuItem && menuItemList.push(
        <Node key={menuItem.id} {...ListItem}> {this._createMenuItemList(menuItem, 0)} </Node>
      );
    } else {

      filteredMenuData.forEach((item: IMenuItemData, index: number) => {
        menuItemList.push(
          <Node key={index} {...ListItem}>
            {this._createMenuItemList(item, index)}
          </Node>
        );
      });
    }
    return menuItemList;
  }

  private _getFromSubMenu(item?: IMenuItemData): IMenuItemData | null {
    const subMenus = item && item.subMenu;
    if (subMenus && subMenus.length > 0) {
      for (let i = 0; i <= subMenus.length - 1; i++) {
        if (subMenus[i].id === this.state.activeMenu) {
          this.setState({ parentMenu: item && item.id, mobileViewLabelText: subMenus[i].linkText });
          return subMenus[i];
        }
        const found = this._getFromSubMenu(subMenus[i]);
        if (found) {
          return found;
        }
      }
    }
    return null;
  }

  private _createMenuItemList(menuItemData: IMenuItemData, parentIndex: number): JSX.Element | null {

    if (menuItemData && menuItemData.subMenu && menuItemData.subMenu.length > 0) {
      if (this.props.isMobileView && this.state.onlyMobile && this.state.activeMenu !== undefined) {
        return (this._renderSubMenu(menuItemData.subMenu, menuItemData.id, false, undefined, parentIndex));
      } else {
        return (
          <React.Fragment>
            {this._renderButtonMenuItem(menuItemData)}
            {this._renderSubMenu(menuItemData.subMenu, menuItemData.id, true, undefined, parentIndex)}
          </React.Fragment>
        );
      }
    } else if (menuItemData && menuItemData.linkText && menuItemData.linkURL && menuItemData.linkURL.length > 0) {
      return (this._renderLinkMenuItem(menuItemData));
    } else if (menuItemData && menuItemData.linkText && !menuItemData.linkURL) {
      return (this._renderSpanMenuItem(menuItemData));
    }

    return null;
  }

  private _renderSubMenu(subMenus?: IMenuItemData[], activeMenu?: number, renderImageSection?: boolean, isMenuOpen?: boolean, parentIndex?: number): JSX.Element | null {
    const { isMobileView, ListItem } = this.props;
    const { openFirstByDefault } = this.state;
    if (activeMenu && this.state.activeMenu !== activeMenu) {
      this.props.context.telemetry.error('Navigation Active menu content is empty, module wont render.');
      return null;
    }

    if (!subMenus || subMenus.length === 0) {
      this.props.context.telemetry.error('Navigation Submenu content is empty, module wont render.');
      return null;
    }

    let isInnerOpen = false;

    let levelClassName: string = '';
    const menuOptions = subMenus && subMenus.map((option: IMenuItemData, idx: number) => {
      let isOpen = option.id === this.state.activeDesktopMenu || isMenuOpen;
      const hasOptions = (option.subMenu && option.subMenu.length > 0);
      let menuItem: JSX.Element | null;
      if (hasOptions && isMobileView && this.state.onlyMobile) {
        menuItem = this._renderButtonMenuItem(option, activeMenu, idx);
      } else if (this.currentLevel === 1 || !hasOptions) {
        if (option.subMenu && option.subMenu.length) {
          menuItem = this._renderDesktopButtonMenuItem(option, this.state.activeDesktopMenu, idx);
        } else {
          menuItem = (option.linkURL
            ? this._renderLinkMenuItem(option, idx)
            : this._renderSpanMenuItem(option)
          );
        }
      } else {
        menuItem = null;
      }
      let subMenu;

      if (hasOptions) {
        if (this.currentLevel >= 2) {
          levelClassName = 'level-3';
          subMenu = this._renderSubMenuDrawer(levelClassName, option, isMobileView && this.state.onlyMobile ? option.id : undefined, false, openFirstByDefault && idx === 0);
        } else if (this.currentLevel <= this.supportedLevel - 1) {
          this.currentLevel++;
          levelClassName = `level-${this.currentLevel.toString()}`;
          subMenu = this._renderSubMenu(option.subMenu, isMobileView && this.state.onlyMobile ? option.id : undefined, false, isOpen, parentIndex);
        }
      }

      if (isOpen) {
        isInnerOpen = isOpen;
      }
      return (
        <Node {...ListItem} key={option.id} className={`ms-nav__list__item  ${isOpen ? 'dont-collapse' : 'collapse'}`} >
          {menuItem}
          {subMenu}
          {/* {isOpen ? subMenu : null } */}
        </Node>
      );
    });
    this.currentLevel++;
    levelClassName = `level-${this.currentLevel.toString()}`;
    if (levelClassName === 'level-2') {
      levelClassName += ' open';
    }
    return (this._renderMenu(levelClassName, menuOptions, activeMenu, renderImageSection, isInnerOpen));
  }
  private _renderSubMenuDrawer(levelClassName: string, menuItem: IMenuItemData, activeMenu?: number, renderImageSection?: boolean, isOpen?: boolean): JSX.Element | null {
    if(menuItem.linkText === "Sale" || menuItem.linkText === "Città Essentials" || menuItem.linkText ==="Sofas & Armchairs"){
      return (
        <Drawer
          collapseProps={
            {
              timeout: 0,
              isOpen: false
            }
          }
          key={menuItem.id}
          className={classnames('ms-nav__drawer_desktop', levelClassName)}
          toggleButtonText={menuItem.linkText || ''}
          toggleButtonTextClick={true}
          selectedStateOptionText={''}
          toggleButtonUrl={this._formatLinkData(menuItem && menuItem.linkURL || '')}
        >
          <div>
            {menuItem.subMenu!.map((menuSubItem: IMenuItemData, index: number) => {
              return null;
            })}
          </div>
        </Drawer>
      );
    } else {
      return (
        <Drawer
          collapseProps={
            {
              timeout: 0,
              isOpen: isOpen || (this.state.isDrawerOpen && this.state.desktopCategoryId === menuItem.id)
            }
          }
          key={menuItem.id}
          className={classnames('ms-nav__drawer_desktop', levelClassName)}
          openGlyph='ms-nav__drawer-open'
          closeGlyph='ms-nav__drawer-close'
          glyphPlacement='end'
          toggleButtonText={menuItem.linkText || ''}
          onToggle={this._bindDesktopCategoryClick(menuItem.id!)}
          toggleButtonTextClick={true}
          selectedStateOptionText={''}
          toggleButtonUrl={this._formatLinkData(menuItem && menuItem.linkURL || '')}
        // onCategoryMouseOver={this._bindChangeImage(menuItem)}
        >
          <div>
            {menuItem.subMenu!.map((menuSubItem: IMenuItemData, index: number) => {
              return this._renderDrawerLink(menuSubItem, index);
            })}
          </div>
        </Drawer>
      );
    }
  }
  private _formatLinkData = (href: MsdynILinkData | string | undefined): string => {
    const domain = this.props.context && this.props.context.request && this.props.context.request.url;
    let absoluteUrl: string = '';
    if (href === undefined) {
      return absoluteUrl;
    }
    const path: string = (href as MsdynILinkData).destinationUrl || (href as string);
    if (domain) {
      absoluteUrl = (new URL(path, domain.requestUrl)).href;
      // Remove domain query string and excessive backslashes
      absoluteUrl = removeDomainQspFromUrl(absoluteUrl, this.props.context.request);
      // As a safeguard, also remove any stray backslashes
      absoluteUrl = absoluteUrl.replace(/\\+/g, '');
    }
    return absoluteUrl;
  }

  // tslint:disable-next-line: no-any
  private _bindDesktopCategoryClick = (desktopCategoryId: number) => (e: any): void => {
    if (this.state.desktopCategoryId !== desktopCategoryId) {
      this.setState({ desktopCategoryId: desktopCategoryId, isDrawerOpen: true });
    } else {
      this.setState({ desktopCategoryId: -1, isDrawerOpen: false });
    }

  }

  private _renderButtonMenuItem(option: IMenuItemData, activeMenu?: number, index?: number): JSX.Element | null {
    const { Button } = this.props;

    return (
      <Node
        key={index}
        {...Button}
        className={`${Button.className} ${option.id === this.state.activeMenu ? 'active' : ''}`}
        onClick={this._handleDropdownToggle(option, activeMenu)}
        aria-haspopup={true}
        aria-expanded={this.state.activeMenu && this.state.activeMenu === option.id ? true : false}
        data-parent={activeMenu}
      >
        {option.linkText}
      </Node>
    );
  }

  private _renderDesktopButtonMenuItem(option: IMenuItemData, activeDesktopMenu?: number, index?: number, isOpen?: boolean): JSX.Element | null {
    const { Button } = this.props;
    return (
      <Node
        key={index}
        {...Button}
        className={`${Button.className} desktop-menu-item`}
        onClick={this._handleDesktopDropdownToggle(option, activeDesktopMenu)}
        aria-haspopup={true}
        aria-expanded={this.state.activeDesktopMenu && this.state.activeDesktopMenu === option.id ? true : false}
        data-parent={activeDesktopMenu}
      >
        {option.linkText}
      </Node>
    );
  }

  // Utility to remove the query string (and everything after '?') from a path
  private _stripQueryString = (path: string): string => {
      return path.split('?')[0];
  }
 
  // Example usage:
  // const cleanPath = this._stripQueryString('/online-navigation-hierarchy/shop/browse/citt-essentials/5637213583.c?domain=www.cittadesign.com/\\\\');
  // cleanPath === '/online-navigation-hierarchy/shop/browse/citt-essentials/5637213583.c'


  private _renderLinkMenuItem(option: IMenuItemData, index?: number): JSX.Element | null {
    const { Link } = this.props;
    const optionVal = option.linkText;
    const linkText = option.linkText ? option.linkText : '';
    this.payLoad.contentAction.etext = linkText;
    const attributes = getTelemetryAttributes(this.telemetryContent, this.payLoad);
    return (
      <Node {...Link} key={index} href={this._stripQueryString(option.linkURL|| '')} {...attributes} onMouseOver={this._renderImage(option)} onClick={onTelemetryClick(this.telemetryContent, this.payLoad, linkText)} >
        {optionVal}
      </Node>
    );
  }
  private _renderSpanMenuItem(option: IMenuItemData, index?: number): JSX.Element | null {
    const { Span } = this.props;
    return (
      <Node key={index} {...Span}>{option.linkText}</Node>
    );
  }

  private _renderMenu(level: string, menuOptions: JSX.Element[], currentItem?: number, renderImageSection?: boolean, isInnerOpen?: boolean): JSX.Element | null {
    const { MenuList } = this.props;
    this.currentLevel = 1;
    if (level === 'level-2 open') {
      setTimeout(() => {
        document.querySelector('.open')?.classList.remove('open');
      }, 0);
    }
    return (
      <Node {...MenuList} className={classnames(MenuList.className, level)}>
        <div className='main-container-Menu'>
          {renderImageSection ?
            <button
              className='btn nav-menu-close'
              aria-label='Close'
              onClick={this._closeSubmenu}
            > X
            </button>
            : null}
          <div className={`container-Menu-item-list`}>
            {menuOptions}
          </div>
          {/* <div className='container-menu-image'>
            {renderImageSection ?
              <Image
                className={''}
                src={this.state.cateGoryImageURL}
                altText={'Image'}
                imageSettings={this._getCategoryImageSettings()}
                gridSettings={
                  {
                    xs: { w: 767 },
                    sm: { w: 991 },
                    md: { w: 1199 },
                    lg: { w: 1599 },
                    xl: { w: 1600 }
                  }
                }
                loadFailureBehavior='empty'
              />
              : null}
          </div> */}
        </div>

      </Node>
    );
  }

  // private _bindChangeImage = (data: IMenuItemData) => (e: React.MouseEvent<Element, MouseEvent>): MouseEvent => {
  //   // console.log('_bindChangeImage', _bindChangeImage)
  //   // const imageUrl = generateImageUrl(data.imageSource, (this.props.context.request).apiSettings) || 'undefined';
  //   // this.setState({ cateGoryImageURL: imageUrl });
  //   return e.nativeEvent;
  // }

  private _renderImage = (data: IMenuItemData) => (): void => {
    // const imageUrl = generateImageUrl(data.imageSource, (this.props.context.request).apiSettings) || 'undefined';
    // this.setState({ cateGoryImageURL: imageUrl });
  }

  private _handleDesktopDropdownToggle = (data: IMenuItemData, parentId?: number) => (e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
    e.preventDefault()

    if (this.state.activeDesktopMenu === data.id) {
      this.setState({
        mobileViewLabelText: data.linkText!,
        parentMenu: parentId,
        activeDesktopMenu: undefined
      });
    } else {
      this.setState({
        mobileViewLabelText: data.linkText!,
        parentMenu: parentId,
        activeDesktopMenu: data.id
      });
    }
  };

  private _handleDropdownToggle = (data: IMenuItemData, parentId?: number) => (e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
    e.preventDefault();
    if (!this.state.onlyMobile) {
      // const headerElem = document.querySelector('.ms-header');
      let activeMeu = (this.state.activeMenu && this.state.activeMenu === data.id!) ? undefined : data.id!;
      let activeDesktopMeu;
      if (this.currentLevel === 1 && this.state.activeMenu !== data.id!) {
        //headerElem && headerElem.classList.toggle('lock-opaque', true);
        // if (activeMeu === this.props.menuItemData[0].id) {
        //   activeDesktopMeu = this.props.menuItemData && this.props.menuItemData[0] && this.props.menuItemData[0].subMenu && this.props.menuItemData[0].subMenu[0].id;
        // }
        if (activeMeu === this.props.menuItemData[1].id) {
          activeDesktopMeu = this.props.menuItemData && this.props.menuItemData[1] && this.props.menuItemData[1].subMenu && this.props.menuItemData[1].subMenu[0].id;
        }
        if (!window.document.body.classList.contains('menu-open')) {
          window.document.body.classList.toggle('fade-open', true);
          setTimeout(function () {
            window.document.body.classList.toggle('fade-open', false);
          }, 1000);
        }
        window.document.body.classList.toggle('menu-open', true);

      } else {
        window.document.body.classList.toggle('menu-open', this.state.activeMenu === undefined ? true : false);
      }

      this.setState({
        activeMenu: activeMeu,
        parentMenu: parentId,
        activeDesktopMenu: activeDesktopMeu
      });
    } else {
      this.setState({
        activeMenu: data.id,
        mobileViewLabelText: data.linkText!,
        parentMenu: parentId
      });

      window.document.body.classList.toggle('menu-open', false);
    }

    this._resetFocus();
  };

  private _resetFocus = () => {
    if (this.state.onlyMobile) {
      setTimeout(() => {
        this.menuNode && this.menuNode.current && this.menuNode.current.focus();
      }, 0);
    }
  }

  // tslint:disable-next-line:no-any
  private _handleClickOutside = (event: any) => {
    if (event.target && event.target.className.indexOf('ms-nav__list__item__button') !== -1) {
      //do nothing if the menu item is selected
    }
    else if (this.menuNode.current && !this.menuNode.current.contains(event.target)) {
      this.setState({ activeMenu: undefined, activeDesktopMenu: undefined, mobileViewLabelText: '' });
      if (event.target.className.indexOf('nav-menu-overlay') !== -1 || (document.getElementsByClassName('level-2') && document.getElementsByClassName('level-2')[0] && !document.getElementsByClassName('level-2')[0].contains(event.target))) {
        window.document.body.classList.toggle('menu-open', false);
        window.document.body.classList.toggle('search-open', false);
      }
    }
  };

  private _closeSubmenu(): void {
    if (!this.state.onlyMobile) {
      this.setState({ activeMenu: undefined, mobileViewLabelText: '' });
      window.document.body.classList.toggle('menu-open', false);
    }
  }
  private _escFunction = (event: React.KeyboardEvent) => {
    if (event.keyCode === this.escapeKey) {
      this._closeSubmenu();
    }
  }
  private _customUpdateViewport(): void {
    this.viewport = this._customGetViewport();
    this.setState({ onlyMobile: this.onlyMobile });
  }
  private _customGetViewport(): GridSize {
    const { context } = this.props;
    if (MsDyn365.isBrowser) {
      if (typeof window !== undefined && window.innerWidth) {
        const gridSettings = context.request.gridSettings;
        if (gridSettings) {
          if (gridSettings.xs && window.innerWidth <= gridSettings.xs.w) {
            return 'xs';
          } else if (gridSettings.sm && window.innerWidth <= gridSettings.sm.w) {
            return 'sm';
          } else if (gridSettings.md && window.innerWidth <= gridSettings.md.w) {
            return 'md';
          } else if (gridSettings.lg && window.innerWidth <= gridSettings.lg.w) {
            return 'lg';
          } else {
            return 'xl';
          }
        }
      }
    }

    return 'xs';
  }
  // private _getCategoryImageSettings(): IImageSettings {
  //   // tslint:disable-next-line: no-unnecessary-local-variable
  //   const imageSettings: IImageSettings = {
  //     viewports: {
  //       xs: { q: 'w=200&h=300&q=80&m=6&f=jpg', w: 200, h: 300 },
  //       sm: { q: 'w=200&h=300&q=80&m=6&f=jpg', w: 200, h: 300 },
  //       md: { q: 'w=350&h=500&q=80&m=6&f=jpg', w: 350, h: 500 },
  //       lg: { q: 'w=400&h=600&q=80&m=6&f=jpg', w: 400, h: 600 },
  //       xl: { q: 'w=500&h=700&q=80&m=6&f=jpg', w: 500, h: 700 }
  //     }
  //   };

  //   return imageSettings;
  // }
}

export default NavigationMenuView;